import { useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  FileText,
  Briefcase,
  Users,
  LayoutDashboard,
  Receipt,
  Settings,
  Wrench,
  Calendar,
  Clock,
  User,
  Shield,
  LogOut,
  MessageSquare,
  Inbox,
  BarChart3,
  BookTemplate,
  Globe,
  DollarSign,
  Moon,
  Sun,
  PlayCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useClientConversations } from "@/hooks/useClientMessages";
import { useOrganization } from "@/hooks/useOrganization";
import { useSubscriptionPlan } from "@/hooks/useSubscriptionPlan";
import { useTeamPermissions, TeamPermissions } from "@/hooks/useTeamPermissions";
import { GrowthPackBadge } from "@/components/GrowthPackBadge";
import { PowerPackBadge } from "@/components/PowerPackBadge";
import { OrganizationSwitcher } from "@/components/OrganizationSwitcher";
import logo from "@/assets/logo.png";
import logoDark from "@/assets/logo-dark.png";
import { Badge } from "@/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";

interface NavItem {
  name: string;
  href: string;
  icon: any;
  permissionKey?: keyof TeamPermissions;
}

const navigation: NavItem[] = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { name: "Calendar", href: "/calendar", icon: Calendar },
  { name: "Appointments", href: "/appointments", icon: Clock, permissionKey: "can_view_appointments" },
  { name: "Clients", href: "/clients", icon: Users, permissionKey: "can_view_clients" },
  { name: "Quotes", href: "/quotes", icon: FileText, permissionKey: "can_view_quotes" },
  { name: "Projects", href: "/projects", icon: Briefcase, permissionKey: "can_view_projects" },
  { name: "Invoices", href: "/invoices", icon: Receipt, permissionKey: "can_view_invoices" },
  { name: "Services", href: "/services", icon: Wrench, permissionKey: "can_view_services" },
  { name: "Messages", href: "/messages", icon: MessageSquare, permissionKey: "can_view_messages" },
  { name: "Time Clock", href: "/time-clock", icon: Clock },
];

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin } = useIsAdmin();
  const { organizationId, hasMultipleOrganizations } = useOrganization();
  const { hasReporting, hasTemplates, hasTeamMembers, isReviewAccount } = useSubscriptionPlan();
  const { permissions, isOwnerOrAdmin } = useTeamPermissions();
  const { data: conversations } = useClientConversations(organizationId);
  const totalUnread = conversations?.reduce((sum, c) => sum + c.unreadCount, 0) || 0;
  const { state, setOpenMobile, isMobile } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { theme, setTheme } = useTheme();

  const closeMobileSidebar = useCallback(() => {
    if (!isMobile) return;
    setOpenMobile(false);
  }, [isMobile, setOpenMobile]);

  // Extra-robust mobile close: closes the Sheet whenever any sidebar menu button is tapped,
  // even if the click lands on a nested element and the Link's onClick doesn't run.
  const handleSidebarClickCapture = useCallback(
    (event: React.MouseEvent) => {
      if (!isMobile) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-sidebar="menu-button"]')) {
        setOpenMobile(false);
      }
    },
    [isMobile, setOpenMobile],
  );

  useEffect(() => {
    // Close the mobile drawer after navigation (covers edge-cases where tap handlers don't fire as expected).
    closeMobileSidebar();
  }, [location.pathname, closeMobileSidebar]);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      navigate("/auth");
    }
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4 space-y-3">
        <Link to="/dashboard" className="flex items-center" onClick={closeMobileSidebar}>
          <img src={theme === "dark" ? logoDark : logo} alt="QuoteSendGo Logo" className="h-8 w-auto" />
        </Link>
        {hasMultipleOrganizations && !isCollapsed && (
          <OrganizationSwitcher />
        )}
        {!isOwnerOrAdmin && !isCollapsed && (
          <Badge variant="secondary" className="w-fit text-xs">
            Team Member
          </Badge>
        )}
      </SidebarHeader>

      <SidebarContent onClickCapture={handleSidebarClickCapture}>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigation
                .filter((item) => {
                  // Owners/admins see everything; members check permissions
                  if (isOwnerOrAdmin || !item.permissionKey) return true;
                  return permissions[item.permissionKey];
                })
              .map((item) => {
                const isActive = location.pathname === item.href;
                const showBadge = item.name === "Messages" && totalUnread > 0;
                const tourId = `nav-${item.name.toLowerCase()}`;
                return (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton asChild isActive={isActive} tooltip={item.name}>
                      <Link to={item.href} onClick={closeMobileSidebar} className="flex w-full items-center justify-between" data-tour={tourId}>
                        <span className="flex items-center gap-2">
                          <item.icon className="h-4 w-4" />
                          <span>{item.name}</span>
                        </span>
                        {showBadge && !isCollapsed && (
                          <Badge variant="destructive" className="ml-auto">
                            {totalUnread}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              {hasReporting && (isOwnerOrAdmin || permissions.can_view_reports) && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/reports"} tooltip="Reports">
                    <Link to="/reports" onClick={closeMobileSidebar} className="flex w-full items-center justify-between">
                      <span className="flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        <span>Reports</span>
                      </span>
                      {!isCollapsed && <PowerPackBadge show={isReviewAccount} />}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {hasTemplates && isOwnerOrAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/settings/templates"} tooltip="Templates">
                    <Link to="/settings/templates" onClick={closeMobileSidebar} className="flex w-full items-center justify-between">
                      <span className="flex items-center gap-2">
                        <BookTemplate className="h-4 w-4" />
                        <span>Templates</span>
                      </span>
                      {!isCollapsed && <PowerPackBadge show={isReviewAccount} />}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {hasTeamMembers && isOwnerOrAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/settings/team"} tooltip="Team">
                    <Link to="/settings/team" onClick={closeMobileSidebar} className="flex w-full items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>Team</span>
                      </span>
                      {!isCollapsed && <GrowthPackBadge show={isReviewAccount} />}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/tutorials"} tooltip="Tutorials">
                  <Link to="/tutorials" onClick={closeMobileSidebar} className="flex w-full items-center gap-2">
                    <PlayCircle className="h-4 w-4" />
                    <span>Tutorials</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {(isOwnerOrAdmin || permissions.can_view_settings) && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/settings"} tooltip="Settings">
                    <Link to="/settings" onClick={closeMobileSidebar} className="flex w-full items-center gap-2" data-tour="nav-settings">
                      <Settings className="h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter onClickCapture={handleSidebarClickCapture}>
        <SidebarSeparator />
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Profile">
              <Link to="/profile" onClick={closeMobileSidebar}>
                <User className="h-4 w-4" />
                <span>Profile</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Inbox">
                <Link to="/admin/inbox" onClick={closeMobileSidebar}>
                  <Inbox className="h-4 w-4" />
                  <span>Inbox</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Admin Panel">
                <Link to="/admin" onClick={closeMobileSidebar}>
                  <Shield className="h-4 w-4" />
                  <span>Admin Panel</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              tooltip={theme === "dark" ? "Light Mode" : "Dark Mode"}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout} tooltip="Logout">
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

